import { useState } from 'react';
import {
  COLOMBIA_CITIES,
  SHIPPING_ZONE_LABELS,
  additionalRateFor,
  classifyZone,
  computeWeightedShippingCost,
  type ShippingConfig,
} from '@/services/shipping';

interface Props {
  config: ShippingConfig;
}

function formatCOP(value: number): string {
  return `$${Math.round(value).toLocaleString('es-CO')}`;
}

// Simulador rápido: elige ciudad + peso y muestra el costo real del flete
// aplicando la misma matriz y fórmula del checkout, sin abrir la tienda.
export default function ShippingSimulator({ config }: Props) {
  const [city, setCity] = useState('');
  const [weight, setWeight] = useState('1');

  const hasCity = city.trim().length > 0;
  const weightNum = Number(weight) > 0 ? Number(weight) : 0;
  const zone = classifyZone(city, config);
  const base = config.matrix[zone].base;
  const total = computeWeightedShippingCost(weightNum, zone, config);
  const extraKg = Math.max(0, Math.ceil(weightNum) - 1);
  const extraCost = total - base;

  const tiers = Array.from({ length: extraKg }, (_, i) => ({
    index: i + 1,
    rate: additionalRateFor(zone, config, i),
  }));

  const scaleRows = [1, 2, 3, 4, 5, 6].map((kg) => ({
    kg,
    cost: computeWeightedShippingCost(kg, zone, config),
  }));

  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50/60 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-calculator-line text-sm"></i>
        </span>
        <div>
          <h3 className="text-sm font-semibold text-foreground-900">Simulador rápido de envío</h3>
          <p className="text-xs text-foreground-500">
            Usa la matriz que tienes en pantalla (incluso sin guardar) para validar el costo.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
            Ciudad de destino
          </label>
          <input
            type="text"
            list="sim-shipping-cities"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Ej: Medellín"
            className="w-full text-sm border border-background-300 rounded-lg px-3 py-2 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
          <datalist id="sim-shipping-cities">
            {COLOMBIA_CITIES.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <div>
          <label className="text-xs font-medium text-foreground-600 mb-1.5 block">
            Peso total (kg)
          </label>
          <input
            type="number"
            min="0"
            step="0.1"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="1"
            className="w-full text-sm border border-background-300 rounded-lg px-3 py-2 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
      </div>

      {!hasCity ? (
        <p className="text-xs text-foreground-500 flex items-center gap-1.5">
          <i className="ri-arrow-up-line"></i>
          Escribe una ciudad para ver el sector detectado y el costo.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-foreground-500">Sector detectado:</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-primary-500 text-white whitespace-nowrap">
              <i className="ri-map-pin-line"></i>
              {SHIPPING_ZONE_LABELS[zone]}
            </span>
          </div>

          <div className="rounded-lg bg-white border border-background-200 p-3.5">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs text-foreground-500">Total del flete</p>
                <p className="text-2xl font-bold text-primary-700">{formatCOP(total)}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-foreground-500">
                  Kilo inicial: <span className="font-medium text-foreground-800">{formatCOP(base)}</span>
                </p>
                <p className="text-[11px] text-foreground-500">
                  Kilos extra: <span className="font-medium text-foreground-800">{extraKg}</span>
                  {extraKg > 0 && (
                    <>
                      {' · '}
                      <span className="font-medium text-foreground-800">{formatCOP(extraCost)}</span>
                    </>
                  )}
                </p>
              </div>
            </div>

            {tiers.length > 0 && (
              <div className="mt-3 pt-3 border-t border-background-100 flex flex-wrap gap-1.5">
                {tiers.map((t) => (
                  <span
                    key={t.index}
                    className="text-[11px] px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800 whitespace-nowrap"
                  >
                    Kg +{t.index}: {formatCOP(t.rate)}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg bg-white border border-background-200 p-3.5">
            <p className="text-xs font-medium text-foreground-600 mb-2.5">
              Escalafón por peso (1 a 6 kg)
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {scaleRows.map((row) => (
                <div
                  key={row.kg}
                  className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-background-50 border border-background-100"
                >
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{row.kg} kg</span>
                  <span className="text-xs font-semibold text-foreground-800 whitespace-nowrap">
                    {formatCOP(row.cost)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}